import { useMemo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { usePublicAppStore } from "@/store";
import { appleIdentityParseOAuthResult } from "@/modules/apple-identity/parse-o-auth-result";
import { localStorageModule } from "@/modules/local-storage";
import { appRoutes } from "@/app/router/appRoutes";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";

export const useAppleOAuthRedirectPageBehavior = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { publicStore } = usePublicAppStore();
  const startedFinalizeEntriesRef = useRef(new Set<string>());
  const consumedStaleFailureResetEntriesRef = useRef(new Set<string>());

  const { stateParam, hasError } = useMemo(() => {
    try {
      const { state, error } = appleIdentityParseOAuthResult({ location });
      return { stateParam: state, hasError: Boolean(error) };
    } catch (error) {
      logger.info({
        message: "Apple OAuth failed or was cancelled",
        info: {
          error: error instanceof Error ? error.message : String(error),
          searchParams: location.search,
        },
      });

      return { stateParam: null, hasError: true };
    }
  }, [location]);

  const state = publicStore.auth.authenticateAccountUsingAppleOAuthStateState;
  const callbackEntryId = useMemo(() => {
    if (!stateParam) {
      return null;
    }

    return `${location.key}:${stateParam}`;
  }, [location.key, stateParam]);

  useEffect(() => {
    if (!callbackEntryId || !stateParam || hasError) {
      return;
    }

    const hasStartedFinalizeForEntry = startedFinalizeEntriesRef.current.has(callbackEntryId);
    const hasConsumedStaleFailureReset =
      consumedStaleFailureResetEntriesRef.current.has(callbackEntryId);

    if (!hasStartedFinalizeForEntry && state.isLoading) {
      return;
    }

    if (!hasStartedFinalizeForEntry && state.failedAt && !hasConsumedStaleFailureReset) {
      consumedStaleFailureResetEntriesRef.current.add(callbackEntryId);
      publicStore.auth.resetAuthenticateAccountUsingAppleOAuthStateState();
      return;
    }

    if (hasStartedFinalizeForEntry || state.isLoading || state.failedAt || state.data) {
      return;
    }

    startedFinalizeEntriesRef.current.add(callbackEntryId);
    publicStore.auth.authenticateAccountUsingAppleOAuthState({
      state: stateParam,
    });
  }, [
    callbackEntryId,
    hasError,
    publicStore.auth,
    state.data,
    state.failedAt,
    state.isLoading,
    stateParam,
  ]);

  useEffect(() => {
    if (!hasError) {
      return;
    }

    toastModule.triggerToast({
      content: "Authentication was cancelled or failed. Please try again.",
    });
    navigate(appRoutes.logIn({}).path);
  }, [hasError, navigate]);

  useEffect(() => {
    if (!publicStore.auth.isAuthenticated) {
      return;
    }

    const asyncLogic = async () => {
      const { pathname, search, hash } = await localStorageModule.readPendingRedirectPath();
      await localStorageModule.clearPendingRedirectPath();

      const defaultRedirectPath = appRoutes.notesList({}).path;
      const targetPathname = pathname ?? defaultRedirectPath;

      if (!pathname) {
        navigate(targetPathname);
        return;
      }

      navigate({
        pathname: targetPathname,
        search: search ?? undefined,
        hash: hash ?? undefined,
      });
    };

    void asyncLogic();
  }, [navigate, publicStore.auth.isAuthenticated]);

  return state;
};
